import React from "react";
import { w2pt, izoom, w2p } from "../../utils/coords";

/**
 * OpeningLayer — renders doors and windows with proper CAD symbols.
 *
 * DOOR: single-line leaf + quarter-circle arc (standard architectural plan symbol)
 * WINDOW: three parallel lines across the wall opening (IS 962 / standard CAD)
 *
 * Both are drawn at the correct wall position using parametric t on the wall edge.
 */
export default function OpeningLayer({ st, selOpeningId, onSelect, zoom }) {
  const s = izoom(zoom);
  const openingsLayer = (st.layers || {}).openings;
  if (openingsLayer?.visible === false) return null;

  return (
    <g>
      {Object.values(st.openings || {}).map(op => {
        const e  = st.edges[op.edgeId]; if (!e) return null;
        const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
        if (!ab || !ba) return null;
        const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
        if (!nA || !nB) return null;

        const pA = w2pt(nA), pB = w2pt(nB);
        const dx = pB.x - pA.x, dy = pB.y - pA.y;
        const L  = Math.sqrt(dx*dx + dy*dy);
        if (L < 1) return null;

        // Unit vectors
        const ux = dx / L, uy = dy / L;   // along wall
        const nx = -uy,    ny = ux;        // perpendicular (into room)

        const T  = w2p(e.thickness || 230) / 2;
        const hw = w2p(op.width) / 2;

        // Opening centre in pixel space
        const cx = pA.x + dx * op.t;
        const cy = pA.y + dy * op.t;

        // Opening endpoints (along wall centreline)
        const ox1 = cx - ux * hw, oy1 = cy - uy * hw; // start of opening
        const ox2 = cx + ux * hw, oy2 = cy + uy * hw; // end   of opening

        const isSel  = op.id === selOpeningId;
        const isWin  = (op.sillHeight || 0) > 0;
        const col    = isWin ? "#60a5fa" : "#4ade80";
        const selCol = "#e8a838";
        const sw     = s * 0.8; // line stroke weight
        const ang    = Math.atan2(dy, dx) * 180 / Math.PI;

        return (
          <g key={op.id} style={{ cursor: "pointer" }}
            onClick={ev => { ev.stopPropagation(); onSelect(op.id); }}>

            {/* ── White-out the wall at the opening ── */}
            <rect
              x={cx - hw} y={cy - T}
              width={hw * 2} height={T * 2}
              fill="#020a14" stroke="none"
              transform={`rotate(${ang},${cx},${cy})`}
            />

            {/* ── Selection highlight ── */}
            {isSel && (
              <rect
                x={cx - hw - s * 2} y={cy - T - s * 2}
                width={hw * 2 + s * 4} height={T * 2 + s * 4}
                fill="none" stroke={selCol}
                strokeWidth={s * 0.5}
                strokeDasharray={`${s * 3},${s * 2}`}
                transform={`rotate(${ang},${cx},${cy})`}
                rx={s}
              />
            )}

            {isWin ? (
              // ── WINDOW: three parallel lines ──────────────────────────────
              <g>
                {/* Outer wall faces at opening */}
                <line x1={ox1} y1={oy1} x2={ox2} y2={oy2}
                  stroke={isSel ? selCol : col} strokeWidth={sw}
                  transform={`translate(${nx*T},${ny*T})`}/>
                <line x1={ox1} y1={oy1} x2={ox2} y2={oy2}
                  stroke={isSel ? selCol : col} strokeWidth={sw}
                  transform={`translate(${-nx*T},${-ny*T})`}/>
                {/* Centre glazing line */}
                <line x1={ox1} y1={oy1} x2={ox2} y2={oy2}
                  stroke={isSel ? selCol : col} strokeWidth={s * 0.4} opacity={0.7}/>
                {/* Sill line (if sill height > 0, draw dashed) */}
                {op.sillHeight > 0 && (
                  <line x1={ox1} y1={oy1} x2={ox2} y2={oy2}
                    stroke={col} strokeWidth={s * 0.3}
                    strokeDasharray={`${s*2},${s*2}`} opacity={0.4}
                    transform={`translate(${nx*T*0.5},${ny*T*0.5})`}/>
                )}
              </g>
            ) : (
              // ── DOOR: leaf + quarter-circle arc ────────────────────────────
              <g>
                {/* Door leaf — line from hinge to free end */}
                <line
                  x1={ox1} y1={oy1}
                  x2={ox1 + nx * hw * 2} y2={oy1 + ny * hw * 2}
                  stroke={isSel ? selCol : col} strokeWidth={sw}
                />
                {/* Quarter-circle arc — radius = door width, centred on hinge */}
                {(() => {
                  const r = hw * 2; // arc radius = door width
                  // Start: door leaf end (perpendicular)
                  const arcEx = ox1 + nx * r;
                  const arcEy = oy1 + ny * r;
                  // End: along wall at full opening
                  const arcFx = ox2;
                  const arcFy = oy2;
                  return (
                    <path
                      d={`M ${arcEx} ${arcEy} A ${r} ${r} 0 0 ${ny > 0 || (ny === 0 && nx < 0) ? 1 : 0} ${arcFx} ${arcFy}`}
                      fill="none"
                      stroke={isSel ? selCol : col}
                      strokeWidth={s * 0.4}
                      strokeDasharray={`${s * 4},${s * 2.5}`}
                      opacity={0.7}
                    />
                  );
                })()}
                {/* Hinge dot */}
                <circle cx={ox1} cy={oy1} r={s * 1.5}
                  fill={isSel ? selCol : col} opacity={0.9}/>
              </g>
            )}

            {/* ── Opening size label ── */}
            {hw * 2 > s * 40 && (() => {
              // Label is offset perpendicular from the wall centreline
              const lx = cx + nx * (T + s * 6);
              const ly = cy + ny * (T + s * 6);
              return (
                <text x={lx} y={ly} textAnchor="middle"
                  fontSize={s * 6.5} dominantBaseline="middle"
                  fill={isSel ? selCol : col + "cc"}
                  fontFamily="'JetBrains Mono',monospace"
                  style={{ pointerEvents: "none" }}
                  transform={`rotate(${ang},${lx},${ly})`}>
                  {isWin ? "W" : "D"} {op.width}
                </text>
              );
            })()}
          </g>
        );
      })}
    </g>
  );
}
